// eslint-disable-next-line no-restricted-imports -- coexistence bridge: styled ThemeProvider for app styled() call sites until they migrate off styled-components; approved by Martin 2026-10-12
import {type DefaultTheme, ThemeProvider} from 'styled-components';

import {ReactNode, useEffect, useMemo} from 'react';

import {mergeDeepRight} from 'ramda';

import {FoundationThemeContext} from '../../hooks/useFoundationTheme';
import {Device} from '../../types/Device';
import {DefaultFoundationTheme, foundationTheme} from '../../utils/foundationTheme';
import {DeviceProvider} from '../DeviceProvider/DeviceProvider';
import {useDevice} from '../DeviceProvider/useDevice';

type DeepPartial<Value> = Value extends (...arguments_: never[]) => unknown
  ? Value
  : Value extends object
    ? {[Key in keyof Value]?: DeepPartial<Value[Key]>} & Record<string, unknown>
    : Value;

export type FoundationThemeOverride = DeepPartial<
  Omit<DefaultFoundationTheme, 'icons' | 'lotties'>
> & {
  icons?: Partial<DefaultFoundationTheme['icons']>;
  lotties?: Partial<DefaultFoundationTheme['lotties']>;
};

export type FoundationProviderProps = {
  theme?: FoundationThemeOverride;
  children?: ReactNode;
};

const mergeFoundationTheme = (theme: FoundationThemeOverride): DefaultFoundationTheme => {
  const {icons: defaultIcons, lotties: defaultLotties, ...defaultTheme} = foundationTheme;
  const {icons, lotties, ...themeWithoutRegistries} = theme;
  const themeToMerge: object = themeWithoutRegistries;

  return {
    ...mergeDeepRight(defaultTheme, themeToMerge),
    icons: {...defaultIcons, ...icons},
    lotties: {...defaultLotties, ...lotties},
  };
};

const SEVERITY_LEVELS = ['informational', 'warning', 'success', 'danger'] as const;
// text.white has its own `--color-text-white`; `--color-white` comes from general.white.
const TEXT_COLORS = [
  'primary',
  'secondary',
  'tertiary',
  'informational',
  'success',
  'warning',
  'danger',
  'link',
] as const;
const GENERAL_COLORS = ['accent', 'separator', 'white', 'black', 'transparent'] as const;

// Mirrors the color and shadow variable names in theme.css; the `--color-text-*` and
// `--color-general-*` aliases there resolve through these base variables. Text and general
// colors are listed explicitly so an app-only key (e.g. text.accent) cannot overwrite a base one.
const getColorCssVariables = (theme: DefaultFoundationTheme) => ({
  ...Object.fromEntries(
    Object.entries(theme.colors.palettes).flatMap(([palette, shades]) =>
      Object.entries(shades).flatMap(([shade, opacities]) =>
        Object.entries(opacities).map(([opacity, value]) => [
          `--color-palettes-${palette}-${shade}-${opacity}`,
          value,
        ])
      )
    )
  ),
  ...Object.fromEntries(TEXT_COLORS.map((name) => [`--color-${name}`, theme.colors.text[name]])),
  ...Object.fromEntries(
    GENERAL_COLORS.map((name) => [`--color-${name}`, theme.colors.general[name]])
  ),
  '--color-text-white': theme.colors.text.white,
  ...Object.fromEntries(
    SEVERITY_LEVELS.flatMap((level) => [
      [`--color-severity-${level}`, theme.colors.severity[level]],
      [`--color-severity-${level}-hover`, theme.colors.severity.hover[level]],
      [`--color-severity-${level}-active`, theme.colors.severity.active[level]],
    ])
  ),
  ...Object.fromEntries(
    Object.entries(theme.shadows).map(([name, value]) => [
      `--shadow-${name.replaceAll('_', '-')}`,
      value,
    ])
  ),
});

const getThemeCssVariables = (theme: DefaultFoundationTheme, device: Device) => ({
  ...getColorCssVariables(theme),
  '--eag-space-step': theme.space.step.toString(),
  '--font-family-sans': theme.fonts.body,
  '--font-family-body': theme.fonts.body,
  '--font-family-heading': theme.fonts.heading,
  '--font-weight-regular': theme.fontWeights.regular.toString(),
  '--font-weight-medium': theme.fontWeights.medium.toString(),
  '--font-weight-bold': theme.fontWeights.bold.toString(),
  '--text-size-large': theme.fontSizes.text.large.toString(),
  '--text-size-base': theme.fontSizes.text.base.toString(),
  '--text-size-small': theme.fontSizes.text.small.toString(),
  '--text-size-x-small': theme.fontSizes.text.xSmall.toString(),
  '--text-size-xx-small': theme.fontSizes.text.xxSmall.toString(),
  '--text-leading-large': theme.lineHeights.text.large.toString(),
  '--text-leading-base': theme.lineHeights.text.base.toString(),
  '--text-leading-small': theme.lineHeights.text.small.toString(),
  '--text-leading-x-small': theme.lineHeights.text.xSmall.toString(),
  '--text-leading-xx-small': theme.lineHeights.text.xxSmall.toString(),
  '--text-weight-default': theme.fontWeights.text.default.toString(),
  '--text-weight-alternative': theme.fontWeights.text.alternative.toString(),
  '--heading-size-1': theme.fontSizes.heading[device][1].toString(),
  '--heading-size-2': theme.fontSizes.heading[device][2].toString(),
  '--heading-size-3': theme.fontSizes.heading[device][3].toString(),
  '--heading-size-4': theme.fontSizes.heading[device][4].toString(),
  '--heading-size-5': theme.fontSizes.heading[device][5].toString(),
  '--heading-size-6': theme.fontSizes.heading[device][6].toString(),
  '--heading-leading-1': theme.lineHeights.heading[device][1].toString(),
  '--heading-leading-2': theme.lineHeights.heading[device][2].toString(),
  '--heading-leading-3': theme.lineHeights.heading[device][3].toString(),
  '--heading-leading-4': theme.lineHeights.heading[device][4].toString(),
  '--heading-leading-5': theme.lineHeights.heading[device][5].toString(),
  '--heading-leading-6': theme.lineHeights.heading[device][6].toString(),
  '--heading-weight-default': theme.fontWeights.heading.default.toString(),
  '--heading-weight-alternative': theme.fontWeights.heading.alternative.toString(),
  '--display-size-1': theme.fontSizes.display[device][1].toString(),
  '--display-size-2': theme.fontSizes.display[device][2].toString(),
  '--display-leading-1': theme.lineHeights.display[device][1].toString(),
  '--display-leading-2': theme.lineHeights.display[device][1].toString(),
  '--display-weight-default': theme.fontWeights.display.default.toString(),
  '--display-weight-alternative': theme.fontWeights.display.alternative.toString(),
  '--letter-spacing-text': theme.letterSpacing.text.base.toString(),
  '--letter-spacing-heading': theme.letterSpacing.heading[device][1].toString(),
  '--letter-spacing-display': theme.letterSpacing.display[device][1].toString(),
});

type ThemeCssVariablesProps = {
  theme: DefaultFoundationTheme;
};

function ThemeCssVariables(props: ThemeCssVariablesProps) {
  const device = useDevice();

  useEffect(
    function syncThemeCssVariables() {
      const rootElement = document.documentElement;
      const cssVariables = getThemeCssVariables(props.theme, device);
      const defaultCssVariables = new Map(
        Object.entries(getThemeCssVariables(foundationTheme, device))
      );
      // Keys the default theme does not map (e.g. an app's own shadows.focus) are skipped.
      const cssVariableOverrides = Object.entries(cssVariables).filter(
        ([name, value]) => defaultCssVariables.has(name) && value !== defaultCssVariables.get(name)
      );
      const previousValues = Object.fromEntries(
        cssVariableOverrides.map(([name]) => [name, rootElement.style.getPropertyValue(name)])
      );

      cssVariableOverrides.forEach(([name, value]) => {
        rootElement.style.setProperty(name, value);
      });

      return function restoreThemeCssVariables() {
        Object.entries(previousValues).forEach(([name, value]) => {
          if (value.length === 0) {
            rootElement.style.removeProperty(name);
          } else {
            rootElement.style.setProperty(name, value);
          }
        });
      };
    },
    [device, props.theme]
  );

  return null;
}

export function FoundationProvider(props: FoundationProviderProps) {
  const mergedTheme = useMemo(() => mergeFoundationTheme(props.theme ?? {}), [props.theme]);

  return (
    // Coexistence bridge for legacy styled-components consumers; remove after their migration.
    // Turn off because of chakra theme - remove when chakra is gone
    // eslint-disable-next-line eslint-js/no-restricted-syntax -- ported legacy coexistence-bridge assertion; approved by Martin 2026-10-12, removed with the bridge
    <ThemeProvider theme={mergedTheme as unknown as DefaultTheme}>
      <FoundationThemeContext.Provider value={mergedTheme}>
        <DeviceProvider>
          <ThemeCssVariables theme={mergedTheme} />
          {props.children}
        </DeviceProvider>
      </FoundationThemeContext.Provider>
    </ThemeProvider>
  );
}
